import { notify } from '@kyvg/vue3-notification'
import dayjs from 'dayjs'
import MobileDetect from 'mobile-detect'
import type { ComputedRef, Ref } from 'vue'
import { defineStore } from 'pinia'
import { isCompanyIp as checkCompanyIp, isNaverBotIp, isDaumCrawlerUa, isDesktopLinuxBrowserUa, type ImageSet } from '../composables/common'
import { useDYAppConfig, useDYCookieDomain } from '../composables/config'
import { $dy__fetch, $dy__fetchWithToken, useDYFetch, useDYFetchWithToken } from '../composables/fetch'
import { useDYMainStore } from './main'
import { useDYAuth } from '../composables/auth'
import { useDYArchiveStore } from './archive'
import { useUtils } from '../composables/utils'
import { findIpForSSR } from '../libs/utils'

class DYUserStoreError extends Error {
}

export type AvailPatnerAllowedFunctionType = 'CourseSalesStatistics' | 'CouponIssueList' | 'CoursePayList' | 'CoursePayDetail'

interface User {
  no: number
  id: string
  name: string
  nickname?: string
  // picture: string
  pictureSet: ImageSet
  phone: string
  adminPhoto?: string
  email: string
  vipGrade: number
  isoCode2?: string
  scope: string[]
  pcsCertify?: boolean
  /**
   * 차단된 다른 사용자. 이 목록에 있는 사용자의 컨텐츠는 노출되지 않아야 한다.
   */
  blockedUsers?: number[]
  ssrStatisticsData?: any

  zip?: string
  address1?: string
  address2?: string
  countUnreadMessage?: number
  /**
   * 자체 로그인 가능한지 여부 (oauth가입자가 아니거나, oauth가입했지만 비번 바꾼 경우)
   */
  isAbleDirectLogin?: boolean,
  /**
   * 인증 키
   */
  authKey?: string,
  /**
   * 인증 여부
   */
  isAuth?: boolean
}

interface TokenInfo {
  expiration: Date
  token: string
  refreshToken?: string
}

export function getOrFail<V>(value: Ref<V | undefined>): ComputedRef<V> {
  return computed(() => {
    if (value.value === undefined) {
      throw new DYUserStoreError('아직 준비되지 않은 기능입니다.')
    }
    return value.value
  })
}

export const useDYUserStore = defineStore('nuxt3-module-doyac-user', () => {
  /**
   * mocking 토큰을 이용해 로그인할 수 있는지를 결정하는 값. 유저용은 `true`를 쓰고 관리자용은 `false`를 써야함. 기본값은 `true`
   */
  const isMockingEnabled = ref(true)

  // 반응성 동기화를 위해 쿠키 객체들을 스토어 최상단에 정의
  // 쿠키 도메인을 .doyac.com 형태로 통일하여 www.doyac.com / .doyac.com 중복 쿠키 문제 방지
  const { API_BASE_URL } = useDYAppConfig()
  const cookieDomain = useDYCookieDomain()

  const cookieToken = useCookie<string | null>('auth._token.laravelPassport', { domain: cookieDomain })
  const cookieExpiration = useCookie<string | null>('auth._token_expiration.laravelPassport', { domain: cookieDomain })
  const cookieStrategy = useCookie<string | null>('auth.strategy', { domain: cookieDomain })
  /**
   * 로그인·가입 후 이동할 경로.
   *
   * 🔴 **이 쿠키는 반드시 이 ref 로 읽고 써야 한다. js-cookie(`Cookies.set`)로 쓰면 안 된다.**
   * `useCookie` ref 는 **만들어진 시점의 값**을 들고 있다. 바깥에서 `document.cookie` 를 직접 고쳐도
   * 이 ref 에 반영되는 건 `cookieStore` 의 **비동기 change 이벤트**를 통해서라, 쓴 직후 동기적으로
   * 읽으면 **낡은 값이 나온다.**
   *
   * 실제로 그렇게 깨진 적이 있다 — `join/step1.vue` 가 `Cookies.set('auth.redirect','/join/finish')`
   * 로 쓰는 바람에 {@link useDYCommonLogin} 의 `getMoveTo()` 가 못 읽어, 가입 후 `/join/finish` 가
   * 아니라 메인으로 튕겼다. 그 페이지에서만 발사되는 **가입 전환(구글·네이버·카카오)이 2026-04-09 부터
   * 2026-09-30 까지 통째로 누락**됐다.
   */
  const cookieRedirect = useCookie<string | null>('auth.redirect')
  const mockingToken = useCookie<string | null>('auth._token.laravelPassport_mocking', { domain: cookieDomain })
  const mockingExpiration = useCookie<string | null>('auth._token_expiration.laravelPassport_mocking', { domain: cookieDomain })

  // 레거시 쿠키 마이그레이션 및 정리:
  // 과거 웹앱 또는 구버전 Flutter 앱이 www. 도메인으로 설정한 쿠키를 처리한다.
  // - .도메인 쿠키가 이미 존재하면: www. 쿠키는 단순 삭제 (브라우저 복원된 오래된 쿠키일 수 있음)
  // - .도메인 쿠키가 비어있으면: www. 쿠키 값을 .도메인으로 마이그레이션 후 삭제
  //   (구버전 Flutter 앱이 www. 도메인으로만 쿠키를 설정하는 경우에 해당)
  // 주의: 구 iOS/Android 네이티브 앱에서는 이 마이그레이션이 access token을 삭제하는 문제가 있으므로
  //       플러터 앱이거나 앱이 아닌 경우(일반 브라우저)에서만 실행한다.
  //       injectBrowserInfo() 호출 후 앱 판단이 완료된 시점에 실행해야 하므로 함수로 분리함.
  function migrateLegacyCookies() {
    if (!import.meta.client || !cookieDomain) return

    // 구 네이티브 앱(플러터가 아닌 iOS/Android 앱)에서는 스킵
    if (!isFlutterApp.value && (isIOSApp.value || isAndroidApp.value)) return

    const parseCookieValue = (name: string): string | null => {
      const escaped = name.replace(/\./g, '\\.')
      const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`))
      return match ? decodeURIComponent(match[1]) : null
    }

    const migrationTargets: [string, typeof cookieToken][] = [
      ['auth._token.laravelPassport', cookieToken],
      ['auth._token_expiration.laravelPassport', cookieExpiration],
      ['auth.strategy', cookieStrategy],
    ]

    for (const [name, target] of migrationTargets) {
      const wwwValue = parseCookieValue(name)
      if (wwwValue && !target.value) {
        target.value = wwwValue
      }
    }

    const legacyCookieNames = [
      'auth._token.laravelPassport',
      'auth._token_expiration.laravelPassport',
      'auth.strategy',
      'auth._token.laravelPassport_mocking',
      'auth._token_expiration.laravelPassport_mocking',
      'auth._token_keepAlive.laravelPassport',
    ]
    for (const name of legacyCookieNames) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    }
  }

  const accessToken = ref<string>()
  const accessTokenExpiration = ref<Date>()
  const browserName = ref<string>()
  const userAgent = ref<string>()
  const isIOS = ref(false)
  const isAndroidOS = ref(false)
  const isWindowsOS = ref(false)
  const isMacOS = ref(false)
  const isAndroidApp = ref(false) // TODO: 유저용 프로젝트에서 작업예정
  const isIOSApp = ref(false) // TODO: 유저용 프로젝트에서 작업예정
  const isFlutterApp = ref(false) // TODO: 유저용 프로젝트에서 작업예정
  const inAppLoading = ref(false) // 인앱 로딩
  const isFirstPage = ref(true)
  const countryCode = ref<string>()
  const userNo = ref<number>()
  const adminPhoto = ref<string>()
  const id = ref<string>()
  const name = ref<string>()
  const nickname = ref<string>()
  // const picture = ref<string>()
  const pictureSet = ref<ImageSet>()
  const pcsCertify = ref<boolean>()
  const phone = ref<string>()
  const email = ref<string>()
  const vipGrade = ref<number>()
  const ssrStatisticsData = ref<any>() // TODO: 유저용 프로젝트에서 작업예정
  const scope = ref<string[]>()
  const androidAppVersion = ref<number>() // TODO: 유저용 프로젝트에서 작업예정
  const iOSAppVersion = ref<number>()
  const flutterAppVersion = ref<number>()

  /**
   * 시도 등록 방식 인앱결제(장바구니 · 업그레이드)가 있는 앱은 **마이퍼스트가이드 하나뿐**이다.
   *
   * 🔴 아래 두 기준 빌드번호(1039 · 1046)는 **마퍼가 앱의 번호**다. 플러터 앱이면 사이트를 가리지 않고
   *   대 보던 때, 도약닷컴 · 도약아트 앱을 마퍼가와 같은 5.7.0 (1054) 로 올리자 이 웹이 두 앱도
   *   인앱결제 앱으로 보고 바로결제 대신 [구매 진행] 을 띄웠다. 두 앱에는 인앱결제 처리가 없어
   *   눌러도 시트만 닫히고 결제가 안 됐다(2026-10-15 닷컴 5.7.0 QA). 1029 까지는 기준 아래라
   *   드러나지 않았을 뿐이다. 세 앱의 빌드번호는 따로 매겨지므로 숫자만으로는 앱을 가를 수 없다.
   */
  const { siteNameEN: SITE_NAME_EN } = useDYAppConfig()
  const hasInAppIntentPurchase = SITE_NAME_EN === 'myfirstguide'

  /**
   * 앱에서 장바구니(조합 결제)를 쓸 수 있는 최소 플러터 빌드번호(마퍼가 앱).
   * @see supportsInAppCart
   */
  const IN_APP_CART_MIN_FLUTTER_VERSION = 1039

  const supportsInAppCart = computed(() => {
    return hasInAppIntentPurchase && isFlutterApp.value
      && (flutterAppVersion.value ?? 0) >= IN_APP_CART_MIN_FLUTTER_VERSION
  })

  /**
   * 앱에서 **업그레이드(맞춤결제)도 시도 등록 방식**으로 결제할 수 있는 최소 플러터 빌드번호.
   * @see supportsInAppUpgrade
   *
   * ▶ 왜 장바구니(1039)와 따로인가
   *   1039 는 «장바구니를 시도 등록으로 산다» 까지였고, 업그레이드는 그 뒤로도 옛 경로에
   *   남아 있었다(쌍 전용 상품 `tgu_원본_대상` + 검증 때 «최근 미결제 맞춤결제» 추측).
   *   그걸 신형으로 옮긴 것이 이 빌드다. 한 숫자로 뭉뚱그리면 1039~ 사용자에게 **앱이 모르는
   *   형태**(`manualPayNo`)가 가서 결제가 아무 일도 없이 끝난다.
   *
   * ▶ 기준이 1046 인 이유
   *   1045 는 2026-09-10 에 **이미 스토어에 올라갔다.** 이 변경이 들어가는 첫 빌드가 1046 이다.
   *   🔴 올릴 때 빌드번호가 1046 을 넘어가도 `>=` 라 그대로 맞는다. 다만 **1046 보다 낮게
   *   올리면 안 된다** — 그 빌드는 이 형태를 이해하지 못한다.
   */
  const IN_APP_UPGRADE_MIN_FLUTTER_VERSION = 1046

  const supportsInAppUpgrade = computed(() => {
    return hasInAppIntentPurchase && isFlutterApp.value
      && (flutterAppVersion.value ?? 0) >= IN_APP_UPGRADE_MIN_FLUTTER_VERSION
  })
  const googleInstanceId = ref<string>() // TODO: 유저용 프로젝트에서 작업예정
  const ip = ref<string>()
  /**
   * 2026-09-08 [신설] **접속자가 지금 어느 나라에서 접속했는가**(ISO-3166-1 alpha-2). 판정 불가면 undefined.
   * 값의 출처는 Cloudflare 가 엣지에서 붙이는 `CF-IPCountry` 헤더다(`injectBrowserInfo` 에서 주입).
   *
   * 🔴 위쪽의 `countryCode` 와 **뜻이 다르다. 절대 섞어 쓰지 말 것.**
   *   · `countryCode`       = 로그인한 회원 **프로필**의 국적(`user.isoCode2`). 로그아웃하면 지워진다.
   *   · `clientCountryCode` = 이번 **요청이 들어온 위치**. 로그인 여부와 무관하다.
   *   (백엔드도 같은 구분을 쓴다: `OssStorageImpl::clientCountry()`)
   */
  const clientCountryCode = ref<string>()
  const myPayment = ref<Record<string, string>>()
  const isAuth = ref(false)
  const authKey = ref<string>()
  /**
   * 읽지않은 쪽지의 개수
   */
  const countUnreadMessage = ref<number>(0)
  const isAbleDirectLogin = ref<boolean>()

  const zip = ref<string>()
  const address1 = ref<string>()
  const address2 = ref<string>()

  const isShowRightMentBanner = ref(true)
  const isShowChannelTalkBanner = ref(true)
  const lastExpirationCheckedTime = ref(new Date())

  /**
   * 카카오 공유 등의 기능을 사용하기 위한 카카오 객체
   */
  const kakao = ref<any>()
  /**
   * 페이스북 공유 등의 기능을 사용하기 위한 페이스북 객체
   */
  const facebook = ref<any>()

  const route = useRoute()

  /**
   * 토큰을 갱신해야하는지 판단하는 메서드.
   * @param cookie
   */
  function shouldRefreshToken() {
    // 리프레시 토큰의 유무는 httpOnly이므로 판단할 수 없음!
    if (cookieToken.value && cookieExpiration.value) {
      if ((new Date()).getTime() > parseInt(cookieExpiration.value)) {
        return true
      }
    }

    return !cookieToken.value || !cookieExpiration.value
  }

  async function refreshToken(): Promise<TokenInfo | null> {
    try {
      const data = await $dy__fetch<Record<string, any>>('/mer/oauth/auto-refresh')

      if (data) {
        return {
          expiration: dayjs().add(data.expires_in, 'second').toDate(),
          token: `Bearer ${data.access_token}`,
          refreshToken: data.refresh_token
        }
      }

      return null
    } catch (e) {
      // 비로그인 유저는 리프레시 토큰이 없으므로 4xx 에러가 정상적인 응답임.
      // $dy__fetch는 4xx에서 throw하므로 여기서 조용히 null을 반환하여 기존 동작을 유지함.
      return null
    }
  }

  async function fetchUser(token: string): Promise<User | null> {
    // 구 방식. 다른 아이디로 로그인할 때 이전 키로 요청되는 문제가 있어서 사용하지 않음. 애초에 이 요청은 useFetch 방식을 쓸 필요가 없음
    // const response = await useDYFetch<User>(`/mer/oauth/user`, { headers: { Authorization: `${token}` }, immediate: false })
    // await response.execute({ dedupe: 'defer' })
    // return response.data.value

    // 신 방식. $fetch를 직접 사용하는 방식으로 변경함.
    const response = await $dy__fetch<User>(`/mer/oauth/user`, { headers: { Authorization: `${token}` } })
    return response
  }

  function injectBrowserInfo() {
    const headers = useRequestHeaders()

    // 2026-09-08 [신설] 접속 국가 판정 — Cloudflare 가 엣지에서 붙이는 `CF-IPCountry`.
    //   🔑 클라이언트가 위조할 수 없다(CF 가 붙이는 값이라 요청자가 손댈 수 없음).
    //      백엔드도 같은 헤더를 쓴다: OssStorageImpl::clientCountry() / TourPaymentManager 등.
    //   🔴 **서버에서만** 읽는다. 클라이언트의 `useRequestHeaders()` 는 빈 객체라
    //      조건 없이 대입하면 하이드레이션 때 값을 지워 버린다.
    //      SSR 에서 넣은 값은 Pinia 페이로드로 클라이언트까지 넘어오므로 다시 읽을 필요가 없다.
    //   ⚠️ 이 값을 SSR 페이로드에 싣는 것이 안전한 전제 = **페이지 HTML 이 엣지 캐시되지 않는 것**.
    //      (실측 2026-09-08: `/v/` 응답이 `cf-cache-status: DYNAMIC` = 미캐시)
    //      혹시 페이지 HTML 을 엣지 캐시하게 되면 남의 국가가 섞이므로 이 주입을 반드시 재검토할 것.
    //   ⚠️ 'XX' 는 CF 가 판정하지 못한 경우이므로 모름(undefined)으로 둔다. 로컬/직접접속도 헤더가 없다.
    if (import.meta.server) {
      const cfCountry = (headers['cf-ipcountry'] || '').toString().toUpperCase()
      clientCountryCode.value = (!cfCountry || cfCountry === 'XX') ? undefined : cfCountry
    }

    let raw = ''
    if (import.meta.server) {
      raw = headers['user-agent']
    } else {
      raw = window.navigator.userAgent
    }

    if (!raw) { return }

    const { ANDROID_APP_PACKAGE_NAME } = useDYAppConfig()

    userAgent.value = raw

    const md = new MobileDetect(raw);
    const ua = raw.toLocaleLowerCase()
    const isSamsungBrowser = ua.search("samsungbrowser") > -1

    browserName.value = '현재 알아내지 못함'
    isIOS.value = md.os() === 'iOS'
    isAndroidOS.value = md.os() === "AndroidOS" || isSamsungBrowser
    isMacOS.value = ua.search("mac") > -1 || ua.search("macintosh") > -1
    isWindowsOS.value = ua.search("windows") > -1 && !isSamsungBrowser

    // 안드로이드 '데스크톱 사이트' 모드 SSR 보정(서버에서만):
    // 데스크톱 모드는 UA가 'X11; Linux x86_64'로 위장되어 위 UA 기반 판별이 모두 false가 된다.
    // 서버에 Sec-CH-UA-Platform 힌트가 'Android'로 오면 SSR 단계에서 isAndroidOS를 맞춰 하이드레이션 불일치를 줄인다.
    // 클라이언트 보정은 플러그인 시점(Pinia 하이드레이션 이전)이라 복원값에 덮어써지므로 여기서 하지 않고,
    // 하이드레이션 이후인 DYDefault.adjustDesktopModeAppleDevice(onBeforeMount)에서 처리한다(iPad 보정과 동일 위치).
    if (import.meta.server && headers['sec-ch-ua-platform']?.replace(/"/g, '') === 'Android') {
      isAndroidOS.value = true
      isWindowsOS.value = false
      isMacOS.value = false
    }

    // 플러터 앱의 경우 특정 상황에서는 header에 버전 정보가 전달되지 않을 수 있으므로 쿠키에서도 판단해야 함
    // 특히 플러터 웹뷰의 컨트롤러에서 직접 reload 함수를 호출하면 헤더가 누락됨
    const flutterCookie = useCookie('dy-flutter-app-version')
    if ((headers['dy-flutter-app-version'] && headers['dy-flutter-app-version'] !== 'undefined')) {
      console.log('headers[\'dy-flutter-app-version\']', headers['dy-flutter-app-version'])
      flutterCookie.value = `${parseInt(headers['dy-flutter-app-version'])}`
    }

    if (flutterCookie.value) {
      isFlutterApp.value = true
      flutterAppVersion.value = parseInt(flutterCookie.value)
    }

    if (headers['dy-android-app-version']) {
      androidAppVersion.value = parseInt(headers['dy-android-app-version'])
    }
    googleInstanceId.value = headers['dy-google-instance-id']
    // 안드로이드 앱 판단. 안드로이드 웹뷰는 요청 헤더에 `x-requested-with`로 해당 앱의 패키지명을 보내므로 이를 통해
    // 앱에서의 요청인지 알 수 있다. 다만 2024.3.18. 기준으로 이 로직만으로는 앱인지 제대로 판명되지 않는 현상이 보고되고
    // 있다. 이를 해결하기 위해 `DYDefault`에서 onMounted 시점에 안드로이드용 자바스크립트 메서드가 있는지 판단해서 다시
    // 할당하게 하고 있음.
    // `ANDROID_APP_PACKAGE_NAME`이 설정된 프로젝트(유저용)에서만 판단한다. 관리자 프로젝트처럼 이 값이 없는(undefined)
    // 경우, SPA 클라이언트에서는 `x-requested-with`도 undefined이므로 `undefined === undefined`로 잘못 true가 되는 문제 방지.
    isAndroidApp.value = !!ANDROID_APP_PACKAGE_NAME && headers['x-requested-with'] === ANDROID_APP_PACKAGE_NAME

    // 플러터 앱에서는 헤더로 iOS 앱 버전 정보를 전달함
    if (headers['dy-ios-app-version']) {
      iOSAppVersion.value = parseInt(headers['dy-ios-app-version'])
      isIOSApp.value = true
    }
    // 기존 앱은 쿠키로 iOS 앱 버전 정보를 전달함
    else {
      const iOSCookie = useCookie('dy-ios-app-version')
      isIOSApp.value = !!iOSCookie.value
      if (iOSCookie.value) {
        iOSAppVersion.value = parseInt(iOSCookie.value)
      }
    }

    // 플러터 앱이면 OS에 따라서 해당 OS 앱으로 판단시키고 쿠키에 버전 정보를 저장하여 백엔드에서도 해당 쿠키로 판단할 수 있도록 함
    if (isFlutterApp.value) {
      // `isIOS`는 여기서는 false가 나올 수 있으므로 쓰지 않아야 함. 안써도 위 로직에서 이미 제대로 판단함
      // isIOSApp.value = isIOS.value
      // 반면 안드로이드는 위 로직에서 제대로 판단되지 못하는 경우가 있으므로 확실성을 기하기 위해 한번 더 판단함
      isAndroidApp.value = isAndroidOS.value

      const androidCookie = useCookie('dy-android-app-version', { domain: cookieDomain })
      androidCookie.value = `${androidAppVersion.value}`

      const iOSCookie = useCookie('dy-ios-app-version', { domain: cookieDomain })
      iOSCookie.value = `${iOSAppVersion.value}`
    }

    // 앱 판단이 완료된 후 레거시 쿠키 마이그레이션 실행
    migrateLegacyCookies()
  }

  async function injectUser() {
    // 강제 접속인 경우에는 강제 접속만 해제함. 참고로 타임스탬프 판단할 때는 getTime()을 써야함.
    if (mockingExpiration.value && parseInt(mockingExpiration.value) < new Date().getTime()) {
      mockingExpiration.value = null
      mockingToken.value = null
    }

    const expiration = isMockingEnabled.value && mockingExpiration.value
      ? mockingExpiration
      : cookieExpiration
    const token = isMockingEnabled.value && mockingToken.value
      ? mockingToken
      : cookieToken

    const tokenInfo: TokenInfo | null = await (async () => {
      if (shouldRefreshToken()) {
        // 리프레시 토큰의 유무는 클라이언트가 판단할 수 없으므로(httpOnly) 쿠키에 있든 없든 서버로 무조건 쏴야함.
        const refreshedToken = await refreshToken()

        if (refreshedToken) { // 토큰이 갱신되면 쿠키에도 반영해야 함.
          token.value = refreshedToken.token
          expiration.value = `${refreshedToken.expiration.getTime()}`
          cookieStrategy.value = 'laravelPassport'

          // 2024.9.23 김PD
          // 백엔드에서 쿠키를 굽기 때문에 굳이 필요하지 않음. 혹시 문제가 생기면 다시 체크할 것
          // if (window.android && window.android.syncToken) { // 안드로이드 앱 동기화
          //   window.android.syncToken(
          //     refreshedToken.token,
          //     refreshedToken.refreshToken ?? '',
          //     refreshedToken.expiration.getTime()
          //   )
          // }

          // 플러터 앱에 동기화
          if (window.FlutterApp?.postMessage) {
            window.FlutterApp.postMessage(JSON.stringify(
              {
                type: 'SYNC_TOKEN',
                args: {
                  'access_token': refreshedToken.token,
                  'refresh_token': refreshedToken.refreshToken,
                  'expires_in': refreshedToken.expiration.getTime()
                }
              }
            ))
          }

          if (window.webkit?.messageHandlers) { // iOS 앱 동기화
            window.webkit.messageHandlers.scriptHandler?.postMessage({
              script: 'syncToken',
              params: [refreshedToken.token, refreshedToken.refreshToken]
            })
          }
        }
        return refreshedToken
      }

      if (expiration.value && token.value) {
        return {
          expiration: new Date(parseInt(expiration.value)),
          token: token.value
        }
      } else {
        return null
      }
    })()

    if (!tokenInfo) {
      return false
    }

    if (new Date() >= tokenInfo.expiration) {
      expiration.value = null
      return false
    }

    try {
      if (tokenInfo.token) {
        const user = await fetchUser(tokenInfo.token)
        if (user) {
          accessToken.value = tokenInfo.token
          accessTokenExpiration.value = tokenInfo.expiration

          // 플러터 앱에 동기화
          if (window.FlutterApp?.postMessage) {
            window.FlutterApp.postMessage(JSON.stringify(
              {
                type: 'SYNC_USER',
                args: user
              }
            ))
          }

          setSignedUser(user)

          if (isPartner.value) {
            const res = await $dy__fetchWithToken<any>(API_BASE_URL+'/api/admin-partner-allowed-functions', { method: 'get', params: { siteName: `{{('${dbDomain}',%l%)}}` } })
            partnerAllowedFunctionList.value = res.items.map((item: any) => item.function)
          }
          return true
        }
      }
      return false
    } catch (error: any) {
      console.log('failed user info...', error)
      $dy__notifyError(error)
      return false
    }
  }

  function setSignedUser(user: User) { //oauthRouter의 mapUser에서 받아오는 정보임
    userNo.value = user.no
    adminPhoto.value = user.adminPhoto
    id.value = user.id
    name.value = user.name
    nickname.value = user.nickname
    // picture.value = user.picture
    pictureSet.value = user.pictureSet

    phone.value = user.phone
    email.value = user.email
    vipGrade.value = user.vipGrade
    countryCode.value = user.isoCode2
    scope.value = user.scope
    pcsCertify.value = user.pcsCertify
    isAbleDirectLogin.value = user.isAbleDirectLogin
    isAuth.value = user.isAuth ?? false
    authKey.value = user.authKey

    // if (user.zip) zip.value = user.zip
    // if (user.address1) address1.value = user.address1
    // if (user.address2) address2.value = user.address2
  }

  function clearUser() {
    accessToken.value = undefined
    accessTokenExpiration.value = undefined

    userNo.value = undefined
    adminPhoto.value = undefined
    id.value = undefined
    name.value = undefined
    nickname.value = undefined
    // picture.value = undefined
    pictureSet.value = undefined
    phone.value = undefined
    email.value = undefined
    vipGrade.value = undefined
    countryCode.value = undefined
    scope.value = undefined
    pcsCertify.value = undefined
    zip.value = undefined
    address1.value = undefined
    address2.value = undefined
    isAuth.value = false
  }

  async function requestLogout() {
    const mainStore = useDYMainStore()

    const _mockingToken = useCookie('auth._token.laravelPassport_mocking', { domain: cookieDomain })
    if (isMockingEnabled.value && _mockingToken.value) { // 강제 접속인 경우에는 강제 접속만 해제함
      _mockingToken.value = null

      const _mockingExpiration = useCookie('auth._token_expiration.laravelPassport_mocking', { domain: cookieDomain })
      _mockingExpiration.value = null

      if (import.meta.client) {
        window.alert('강제접속 해제되었습니다.')
        location.reload()
      }
    } else {
      mainStore.isLoading = true
      const response = await useDYFetch<any>('/mer/logout', { immediate: false })
      await response.execute({ dedupe: 'defer' })
      mainStore.isLoading = false

      cookieToken.value = null
      cookieExpiration.value = null
      cookieRedirect.value = null
      cookieStrategy.value = null

      clearUser()

      // 안드로이드나 iOS 앱의 경우 앱에서도 로그아웃되도록 앱 전용 스크립트를 호출해야함.
      if (window.android && window.android.logout) {
        window.android.logout()
      }
      if (window.webkit && window.webkit.messageHandlers.scriptHandler) {
        window.webkit.messageHandlers.scriptHandler?.postMessage({
          script: 'oAuthLogout',
          params: []
        })
      }
      if (window.FlutterApp?.postMessage) {
        window.FlutterApp.postMessage(JSON.stringify(
          {
            type: 'LOGOUT'
          }
        ))
      }

      notify({
        text: '성공적으로 로그아웃했습니다.'
      })
    }
  }

  const { isPartner } = useDYAuth()
  const { dbDomain } = useDYAppConfig()
  const partnerAllowedFunctionList = ref<string[]>([])

  /**
   * 사용자와 관련된 각종 정보를 초기화(준비)하는 메서드. SSR 환경에서 동작시키면 에러가 발생하므로 반드시 클라이언트 환경에서 동작시켜야 함.
   */
  async function init() {
    const archiveStore = useDYArchiveStore()
    if (archiveStore.ssrStatisticsData.ip) {
      // console.log('ssrStatisticsData.ip 발견', archiveStore.ssrStatisticsData.ip)
      ip.value = archiveStore.ssrStatisticsData.ip
    }
    
    // 🔴 2026-10-07 «휴먼 인증이 필요한 IP» 면 `/verify-human` 으로 보내던 분기를 뺐다.
    //   그 페이지는 «캡차만 풀면 비밀번호 없이 해제» 되는 구멍이라 서버와 함께 지웠다. 소프트 차단 IP 도
    //   사이트를 그대로 쓰고, 로그인할 때만 보안 확인을 받는다(로그인 화면의 Turnstile 위젯).
    const response = await useDYFetch<string>(API_BASE_URL+'/api/ip', { immediate: false })
    await response.execute({ dedupe: 'defer' })
    if (response.error.value) {
      // 구 서버(2026-10-07 배포 전)는 소프트 차단 IP 에 401 RECAPTCHA_REQUIRED 를 준다 — 막지 않고 IP 만 받는다.
      // 서버가 먼저 배포되면 오지 않는 응답이라, 배포 순서가 뒤바뀌어도 안전하게 두는 것이다.
      if (response.error.value.data?.resultCode === 'RECAPTCHA_REQUIRED') {
        ip.value = response.error.value.data.ip
      } else {
        $dy__showPageError('일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.', route)
      }
    } else {
      if (response.data.value) {
        ip.value = response.data.value
      }
    }

    isPrepared.value = true
  }

  const availPartnerAllowedFunction = (functionName: AvailPatnerAllowedFunctionType) => {
    return partnerAllowedFunctionList.value.some((item) => item.includes(functionName))
  }

  /**
   * Laravel Passport 토큰 만료 시간을 반환하는 게터
   */
  const tokenExpiration = computed(() => { //목킹은 관리자 전용이며 실제 본인의 유효시간이 아니므로 무시
    return cookieExpiration.value ? parseInt(cookieExpiration.value) : null
  })

  /**
   * Laravel Passport 토큰 만료 시간을 Date 객체로 반환하는 게터
   */
  const tokenExpirationDate = computed(() => {
    const exp = tokenExpiration.value
    return exp ? new Date(exp) : null
  })

  /**
   * Laravel Passport 토큰이 만료되었는지 확인하는 게터
   */
  const isTokenExpired = computed(() => {
    const exp = tokenExpiration.value
    if (!exp) return true
    return new Date().getTime() > exp
  })

  const isHuman = computed(() => {
    if (userAgent.value && (userAgent.value.includes('Postman'))) { // 포스트맨
      return false
    }

    if (ip.value && isNaverBotIp(ip.value)) { // 네이버 스크랩 엔진 (IP 대역 기반)
      return false
    }

    if (userAgent.value && (userAgent.value.includes('naver.me/scrap') || userAgent.value.includes('Yeti') || userAgent.value.includes('Naver-InspectionTool') || userAgent.value.includes('NaverSearchAdvisor'))) { // 네이버 스크랩 엔진
      return false
    }
    if (userAgent.value && (userAgent.value.includes('Googlebot')
      || userAgent.value.includes('Storebot-Google')
      || userAgent.value.includes('Google-InspectionTool')
      || userAgent.value.includes('GoogleOther')
      || userAgent.value.includes('Google-Extended')
      || userAgent.value.includes('APIs-Google')
      || userAgent.value.includes('AdsBot-Google')
      || userAgent.value.includes('Mediapartners-Google')
      || userAgent.value.includes('Google-Safety'))) { // 구글 스크랩 엔진
      return false
    }
    if (userAgent.value && (userAgent.value.includes('HeadlessChrome'))) { // 헤드리스 크롬. pageres 캡쳐용도일때
      return false
    }
    if (userAgent.value &&
      (userAgent.value.includes('msnbot') || userAgent.value.includes('Slurp') || userAgent.value.includes('Daumoa')
        || userAgent.value.includes('ia_archiver') || userAgent.value.includes('aolbuild') || userAgent.value.includes('teoma')
        || userAgent.value.includes('Baiduspider') || userAgent.value.includes('Twitterbot') || userAgent.value.includes('facebookexternalhit')
        || userAgent.value.includes('Facebot') || userAgent.value.includes('bingbot') || userAgent.value.includes('LinkedInBot')
        || userAgent.value.includes('Applebot') || isDaumCrawlerUa(userAgent.value) || userAgent.value.includes('DuckDuckBot')
        || userAgent.value.includes('kakaotalk-scrap') || userAgent.value.includes('PetalBot'))) { // msn,yahoo,daum 등등
      return false
    }

    // 2026-10-06 [센트리 NUXT3-DOYACWEB-10E / NUXT3-DOYACART-QP] 데스크톱 리눅스·크롬OS 브라우저도 사람으로 본다.
    //   종전엔 위 OS 목록에 없어서 사람인데도 봇 화면을 받았다(경위·판정 기준은 isDesktopLinuxBrowserUa 주석).
    //   ⚠️ OS 플래그(isWindowsOS 등)는 건드리지 않는다 — 플레이어 선택·원격지원 화면처럼 OS 로 갈리는 곳은 종전 그대로다.
    return isWindowsOS.value || isMacOS.value || isAndroidOS.value || isIOS.value || isAndroidApp.value || isIOSApp.value
      || (!!userAgent.value && isDesktopLinuxBrowserUa(userAgent.value))
  })

  const isCompanyIp = computed(() => ip.value ? (checkCompanyIp(ip.value) || process.dev) : false)

  /**
   * 2026-09-29 [신설] 지금 **관리자가 이 회원 계정으로 강제접속**해 있는가.
   *
   * `isMockingEnabled` 는 「강제접속 토큰을 쓸 수 있는 사이트인가」라는 **설정값**이라(유저용은 항상 true)
   * 그것만으로는 지금 강제접속 중인지 알 수 없다. 실제 상태는 mocking 토큰이 있느냐다.
   *
   * 쓰임 — 광고 전환에서 사내 IP 를 뺄 때 이걸 **예외**로 둔다. 고객이 결제를 너무 어려워하면
   * 직원이 그 아이디로 강제접속해 대신 결제해 주는 일이 있는데, 그건 **실제 고객 매출**이라
   * 사무실에서 눌렸다는 이유만으로 빼면 광고 성과가 거꾸로 과소집계된다(2026-09-29 사용자 지적).
   */
  const isMocking = computed(() => !!(isMockingEnabled.value && mockingToken.value))

  /**
   * 2026-09-08 [신설] 접속자가 (Cloudflare 판정상) 중국 본토에 있는가. **모르면 false**.
   *
   * 중국에서는 구글·페이스북 계열이 통째로 막혀 관련 SDK 가 응답 없이 타임아웃된다.
   * 그런 기능을 아예 그리지 않는 판단에 쓴다(예: DYShare 의 페이스북 공유).
   * 🔴 «모르면 막지 않는다» 가 원칙 — 판정 불가(undefined)를 중국으로 취급하면
   *   CF 를 안 거치는 환경(로컬·직접접속)에서 멀쩡한 기능이 사라진다.
   */
  const isChina = computed(() => clientCountryCode.value === 'CN')

  const isSignedIn = computed(() => {
    if (accessTokenExpiration.value) {
      return !!id.value && lastExpirationCheckedTime.value.getTime() < accessTokenExpiration.value.getTime()
    }
    return false
  })

  const isPrepared = ref(false)

  const ready = () => new Promise<boolean>((resolve) => {
    if (import.meta.server) {
      throw new DYUserStoreError('SSR에서 지원되지 않는 기능입니다.')
    }

    // console.log('ready called...')
    if (isPrepared.value) {
      // console.log('userStore is already prepared... return true at once')
      resolve(true)
    } else {
      // console.log('userStore is not prepared yet... wait for preparation...')
      const off = watch(isPrepared, (value) => {
        if (value) {
          // console.log('userStore is now ready... resolve true')
          resolve(true)
          off()
        }
      })
    }
  })

  return {
    init,
    ready,
    injectBrowserInfo,
    injectUser,
    clearUser,
    requestLogout,
    /**
     * 스토어를 사용할 준비가 되었는지 여부. `init` 메서드 호출이 완료되어야 `true`가 된다.
     */
    isPrepared,
    accessToken,
    accessTokenExpiration,
    browserName,
    userAgent,
    isIOS,
    /**
     * 안드로이드 OS 여부. 클라이언트가 브라우저를 강제로 데스크탑 상태로 인식시키는 옵션을 사용하면 안드로이드로
     * 인식되지 않으므로 주의할 것. 이 경우 `isWindowsOS`, `isMacOS` 프로퍼티를 종합적으로 판단해야 한다.
     */
    isAndroidOS,
    /**
     * Mac OS 여부. User Agent에서 'mac' 문구가 포함되어 있는지를 따지는 방식이다.
     */
    isMacOS,
    /**
     * 윈도우즈 OS 여부. User Agent에서 'windows' 문구가 포함되어 있는지를 따지는 방식이다. 안드로이드에서 강제로
     * 데스크탑으로 인식시킨 경우에는 'windows' 문구가 포함되지 않아 `false`가 반환된다.
     */
    isWindowsOS,
    /**
     * 사람에 의한 요청인지 여부. 사람인지를 판단하는 기준은 OS(Windows, Mac, Android, iOS)와 앱 여부이다.
     */
    isHuman,
    /**
     * [SSR 전용. 작업예정] 안드로이드 앱 여부. 안드로이드 앱은 WebView를 사용하는데 WebView 요청인 경우 X-Requested-With 요청 헤더에 앱의
     * 패키지 이름을 함께 보내오는데 이 값의 유무를 가지고 앱 여부를 판단한다.
     */
    isAndroidApp,
    isIOSApp,
    isFlutterApp,
    iOSAppVersion,
    inAppLoading,
    isFirstPage,
    isCompanyIp,
    clientCountryCode,
    isChina,
    countryCode,
    isSignedIn,
    userNo,
    id,
    adminPhoto,
    name,
    nickname,
    // picture,
    pictureSet,
    pcsCertify,
    phone,
    email,
    vipGrade,
    scope,
    /**
     * [SSR 전용. 작업예정] 안드로이드 버전. 버전 코드 260 이후부터 지원
     */
    androidAppVersion,
    /**
     * [SSR 전용. 작업예정] 플러터 앱 버전. 플러터 앱인 경우 지원
     */
    flutterAppVersion,
    /**
     * 앱에서 **장바구니를 쓸 수 있는가** (마이퍼스트가이드 인앱결제, 2026-08-27 신설).
     *
     * 앱에서는 스토어를 거치지 않고 결제할 수 없어(스토어 정책) 장바구니 진입점을 곳곳에서
     * 막아 뒀었다. 담아 봐야 결제할 방법이 없었기 때문이다.
     *
     * 이제 **시도 등록 방식**(결제 전 서버가 레코드를 만들고 여러 상품을 한 번에 청구)이
     * 조합 결제를 열었으므로, 그 방식을 이해하는 앱 버전부터 장바구니를 다시 연다.
     *
     * 🔴 기준이 1039 인 이유 — 1038 은 그 기능이 들어가기 **전** 빌드이고 이미 스토어 심사
     *    중이다. 실사용자 손에 1038 이 있을 수 있어, 그 앱에 장바구니를 열어 주면 담고도
     *    결제하지 못한다.
     *
     * ⚠️ 판정을 여기(스토어)에 둔 이유는 **진입점이 여러 모듈에 흩어져 있기 때문**이다
     *    (DYActionbar·DYRightPanel·DYLayerLR·DYBottomFixedMenu…). 각자 조건을 갖게 두면
     *    한 곳만 열려 "메뉴엔 있는데 다른 데선 없는" 상태가 된다.
     */
    supportsInAppCart,
    supportsInAppUpgrade,
    /**
     * [SSR 전용. 작업예정] FCM 수신에 사용되는 식별자. 보통 안드로이드 앱에서 사용하지만 iOS에서도 사용할 수
     * 있으므로 이 값의 유무를 가지고 안드로이드 앱 여부를 판단해서는 안된다.
     */
    googleInstanceId,
    ip,
    /**
     * @deprecated  서버 미들웨어에서의 저장용으로만 사용하고 다른데서는 쓰지말것!
     */
    ssrStatisticsData,
    myPayment,

    zip,
    address1,
    address2,
    isShowRightMentBanner,
    isShowChannelTalkBanner,
    countUnreadMessage,
    isAbleDirectLogin,
    lastExpirationCheckedTime,
    isMockingEnabled,
    isMocking,
    kakao,
    facebook,
    partnerAllowedFunctionList,
    isAuth,
    authKey,
    availPartnerAllowedFunction,
    tokenExpiration,
    tokenExpirationDate,
    isTokenExpired,
    cookieToken,
    cookieExpiration,
    cookieStrategy,
    cookieRedirect,
    memberJoin: useCookie('memberJoin', { maxAge: 60 * 60 * 24 * 365, path: '/', secure: true }),
    mockingToken,
    mockingExpiration
  }
})